import { ArrowForwardIcon, ArrowRightIcon, EditIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Flex,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Text,
  useToast,
  VStack
} from '@chakra-ui/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import React, { MutableRefObject, useEffect, useState } from 'react';

import { CarretRightIcon } from '../../../../../assets/icons/CarretRightIcon';
import { PencilIcon } from '../../../../../assets/icons/PencilIcon';
import {
  getConsents,
  requestAllConsentsByEv
} from '../../../../../services/previdencia-individual/carteira_previdencia.service';

type BatchLgpdModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccessRequest?: () => void;
};
export const BatchLgpdModal = ({
  isOpen,
  onClose,
  onSuccessRequest
}: BatchLgpdModalProps) => {
  const toast = useToast();
  const [requestType, setRequestType] = useState<'email' | 'celular'>();
  const { data, isLoading } = useQuery(
    ['query-total-consents'],
    () => getConsents(),
    {
      refetchOnWindowFocus: false,
      enabled: !!isOpen,
      onSuccess: (data) => {
        console.log({ data });
      },
      onError: (err) => console.log({ err })
    }
  );
  useEffect(() => {
    console.log({ requestType });
  }, [requestType]);
  const mutate = useMutation({
    mutationFn: ({ type }: { type?: 'email' | 'celular' }) =>
      requestAllConsentsByEv({ type }),
    onSuccess: (_) => {
      handleClose();

      toast({
        status: 'success',
        title: 'Solicitação efetuada com sucesso!',
        //description: 'Tente novamente mais tarde!',
        isClosable: true
      });
      if (onSuccessRequest) onSuccessRequest();
    },
    onSettled: (data, error) => {
      if (error) {
        console.log({ error });
        toast({
          status: 'error',
          title: 'Ops! Ocorreu um erro ao solicitar os consentimentos.',
          description: 'Tente novamente mais tarde!',
          isClosable: true
        });
      }
    }
  });
  const handleClose = () => {
    onClose();
  };
  const consentRequestAvailable =
    data?.filter((el: any) => el.status === 'NOT_SENT').length ?? 0;
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent maxW={500}>
        <ModalHeader>Solicitar consentimento</ModalHeader>
        <ModalCloseButton />
        <Divider />
        <ModalBody p={5} _focus={{ outline: '0 !important' }}>
          <VStack gap={3} width="100%">
            <Flex
              width="100%"
              p={4}
              textAlign="center"
              borderRadius="full"
              bg={'#F3F3F3'}
              color="black"
              justifyContent={'center'}
            >
              {isLoading ? (
                <CircularProgress />
              ) : (
                <Text as={'h2'} fontWeight="bold">
                  {consentRequestAvailable === 0
                    ? 'Você já solicitou o consentimento a todos seus clientes.'
                    : `Total de contatos selecionados: ${consentRequestAvailable}`}
                </Text>
              )}
            </Flex>
            <Box width={'100%'} border="1px solid #D8D8D8" borderRadius="md">
              <Box p={3}>
                <Text as={'h2'} fontWeight="bold">
                  Selecione uma opção para solicitar o consentimento:
                </Text>
              </Box>
              <Divider />

              <RadioGroup
                name="type"
                p={3}
                isDisabled={consentRequestAvailable === 0}
                onChange={(value: 'email' | 'celular') => setRequestType(value)}
              >
                <VStack alignItems="start">
                  <Radio
                    colorScheme={'orange'}
                    value="email"
                    borderColor={'#FD6520'}
                    _focus={{ outline: '0 !important' }}
                  >
                    Solicitar através dos e-mails cadastrados
                  </Radio>
                  <Radio
                    colorScheme={'orange'}
                    value="celular"
                    borderColor={'#FD6520'}
                    _focus={{ outline: '0 !important' }}
                  >
                    Solicitar via SMS dos telefones cadastrados
                  </Radio>
                </VStack>
              </RadioGroup>
            </Box>
          </VStack>
          <Flex justifyContent={'flex-end'} mt={3}>
            <Button
              _focus={{ outline: '0 !important' }}
              rightIcon={<CarretRightIcon />}
              colorScheme="orange"
              px={6}
              py={5}
              disabled={consentRequestAvailable === 0 || !requestType}
              isLoading={mutate.isLoading}
              loadingText="Enviando..."
              onClick={() => mutate.mutate({ type: requestType })}
              //variant="outline"
            >
              Solicitar consentimento
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

type CustomFormControlProps = {
  label: string;
  defaultValue?: string;
  type?: React.HTMLInputTypeAttribute;
};

const CustomFormControl = React.forwardRef<
  HTMLInputElement,
  CustomFormControlProps
>((props, ref) => {
  const [inputDisabled, setInputDisabled] = useState(true);

  const handleInputEdit = () => {
    setInputDisabled(false);
    setTimeout(() => {
      if (ref != null && typeof ref !== 'function' && ref.current !== null) {
        ref.current?.focus();
        ref.current?.select();
      }
    }, 100);
  };
  return (
    <Box w="100%">
      <InputGroup
        borderColor={'#929292'}
        _after={{
          content: `'${props.label}'`,
          fontSize: '0.7rem',
          position: 'absolute',
          top: -2,
          left: 13,
          bg: '#FFFFFF',
          px: 2,
          zIndex: 100,
          color: '#929292'
        }}
      >
        <Input
          type={props.type}
          ref={ref}
          placeholder={props.defaultValue}
          disabled={inputDisabled}
        />
        <InputRightElement bg={'transparent'}>
          <IconButton
            _focus={{ outline: '0 !important' }}
            variant={'link'}
            aria-label="Editar"
            icon={<PencilIcon />}
            bg="transparent"
            onClick={handleInputEdit}
          />
        </InputRightElement>
      </InputGroup>
    </Box>
  );
});

CustomFormControl.displayName = 'CustomFormControl';
